import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * Area `optimisation-review` of the console gateway — owned by plan-002 unit **RB-30** (design §15.6.10).
 *
 * Optimisation review (P14-07's screens), over the five routes RB-30 added. A separate area from
 * `optimisation` (gaps and conversation flags). The register is EMPTY: every operation is served.
 *
 * ── WHAT THE SCREEN SEES WHILE THE WORKER IS NOT DEPLOYED (D3) ───────────────────────────────
 * Starting a review records it and queues it on `optimisation_review.run`; the review runs behind
 * the frontier wall in the factory, and until plan-003 deploys that consumer it stays `idle`. The
 * API says so as a fact (`queued`); this slice returns the review with `state: "idle"`, which is
 * what the panel renders. No finding is invented to fill the wait.
 *
 * ── THE MAPPING (design §15.5.2) ─────────────────────────────────────────────────────────────
 *   * The quote's depth cards take their name, dek and points from `DEPTH_COPY` (a product
 *     constant that stays in the console) and their price and `unavailable_reason` from the API,
 *     matched by depth. A depth the API did not price is a malformed response, never a card with a
 *     guessed price.
 *   * `billing` becomes the view's discriminated union; a `wetalk` payer with no recorded reason is
 *     a malformed response, because that state cannot exist.
 *   * `affectedText` ("14 calls") is worded here from `affected_conversation_count`; `startedText`
 *     and `finishedText` through `formatWhen`. A finding with no recorded evidence or no worded fix
 *     says so in words rather than rendering an empty line.
 *
 * ── THE Idempotency-Key ON START ─────────────────────────────────────────────────────────────
 * The API requires one (design §15.5.4). `startOptimisationReview`'s signature carries no key and
 * this slice may not change the view type, so it mints a fresh UUID per invocation: a replay of
 * the SAME HTTP request is recognised, and two deliberate starts are two reviews. Protection from a
 * double click is the panel's (its button is disabled while the mutation is pending). A key the
 * screen chooses per intent — as `createAccount`'s is — would be better; recorded as a design issue.
 *
 * ── DRAFTS (plan-015 DRF, gap G9, ADR 0043) ──────────────────────────────────────────────────
 * Publishing a review now CREATES A DRAFT ("Create draft vN with N fixes"); `publishOptimisationReview` keeps its
 * name and its view (the response keeps `live_version_seq`, unchanged by a draft). Two methods are added for the
 * Versions tab and the Review tab: `listAgentVersionLineage` (each version's `base_agent_version_id`, whether that
 * base is live, and where a draft came from — `answers` or `optimisation_review`, with the review) and
 * `publishDraftAgentVersion` (the Versions publish with `acknowledge_base_change`, for a draft whose base is no
 * longer live — `409 draft_base_changed` otherwise, read by {@link draftBaseChangedOf}).
 *
 * Both are required on `ConsoleGateway` (plan-015 INT-A, 2026-10-09): `apiGateway` carries them (this slice is spread
 * into it) and the sandbox gateway spreads {@link draftLineageSandbox} — it records no base, so it publishes a draft
 * through `publishAgentVersion` and never answers `draft_base_changed`.
 *
 * ── THE ENGINE (plan-032 WAPP-OR, design §43.9, §43.11; AMENDED 2026-10-28) ──────────────────
 * The factory runs a started review now, so the "while the worker is not deployed" note above is history: a review
 * goes `idle` → `running` → `done` or `failed`, and `queued.awaiting` is `"factory"`. This slice reads what the engine
 * records — `failure_reason`, `conversation_read_count`, and per finding `category`, `fix_kind`, `position`,
 * `advice_target` and `evidence_line` (`shown` with its lines, `withheld`, `gone`; each line's `started_at` worded
 * through `formatWhen`) — and adds `listOptimisationReviews` (`GET /v1/agents/{voice_agent_id}/optimisation-reviews`,
 * newest first), which the panel reads to restore the newest review after a reload. A finding's `affectedText` is now
 * "N of M conversations" (of those the review read) once the read count is known. The quote's reasons gain
 * `not_available_yet` and `not_offered`. Findings come in `position` order from the api; this slice keeps that order.
 */
import { ApiError, assertOk, type WeTalkApiClient } from "@wetalk/api-client";
import { money, type CurrencyCode } from "@wetalk/domain";

import type {
  ConsoleGateway,
  OptimisationAdviceTargetView,
  OptimisationFindingDecisionView,
  OptimisationFindingEvidenceLineView,
  OptimisationFindingView,
  OptimisationFixKindView,
  OptimisationReviewBillingView,
  OptimisationReviewDepthOptionView,
  OptimisationReviewDepthView,
  OptimisationReviewFailureReasonView,
  OptimisationReviewPageView,
  OptimisationReviewQuoteView,
  OptimisationReviewScopeView,
  OptimisationReviewStateView,
  OptimisationReviewSummaryView,
  OptimisationReviewUnavailableReasonView,
  OptimisationReviewView,
  PublishedOptimisationReviewView,
  StartOptimisationReviewRequestView,
} from "../gateway.js";
import { DEPTH_COPY, affectedOfReadText } from "../optimisation-review-view.js";
import {
  MalformedResponseError,
  readArray,
  readBoolean,
  readRecord,
  readText,
  readWholeNumber,
} from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

declare module "../gateway.js" {
  interface ConsoleGateway {
    getOptimisationReviewQuote(
      voiceAgentId: string,
      scope: OptimisationReviewScopeView,
    ): Promise<OptimisationReviewQuoteView>;
    /**
     * Starts a review. `payer: "wetalk"` needs a recorded reason and is refused without one. plan-032: Ultra is not
     * offered yet (OD-1) and a person's start is refused while the api switch is off (OD-6) — both `422
     * optimisation_review_unavailable`; a second start while one is open is `409 optimisation_review_already_open`.
     */
    startOptimisationReview(
      request: StartOptimisationReviewRequestView,
    ): Promise<OptimisationReviewView>;
    getOptimisationReview(optimisationReviewId: string): Promise<OptimisationReviewView>;
    /**
     * plan-032 (design §43.9): a VoiceAgent's reviews, newest first, one page at a time (`cursor` from the page
     * before; `null` for the first). The panel reads the first page to restore the newest review after a reload.
     */
    listOptimisationReviews(voiceAgentId: string, cursor: string | null): Promise<OptimisationReviewPageView>;
    decideOptimisationFinding(
      optimisationReviewId: string,
      optimisationFindingId: string,
      decision: OptimisationFindingDecisionView,
    ): Promise<OptimisationReviewView>;
    /**
     * Saves the accepted fixes as a draft version (plan-015 DRF); the live sequence does not move.
     *
     * `expectedLiveVersionSeq` is required for the reason `publishAgentVersion`'s is: the server
     * writes `UPDATE … WHERE live_version_seq = $expected`, so a stale sequence is a 409 the
     * screen resolves by refetching, never by retrying with the same number.
     */
    publishOptimisationReview(
      optimisationReviewId: string,
      expectedLiveVersionSeq: number,
    ): Promise<PublishedOptimisationReviewView>;
    /**
     * plan-015 DRF: what each version was made from (`GET …/versions`, the base fields).
     */
    listAgentVersionLineage(voiceAgentId: string): Promise<readonly AgentVersionLineageView[]>;
    /**
     * plan-015 DRF: publish a draft, saying whether the person saw that its base is no longer live
     * (`acknowledge_base_change`). Without the acknowledgement such a publish is `409 draft_base_changed`
     * ({@link draftBaseChangedOf}). Answers the new live sequence.
     */
    publishDraftAgentVersion(
      voiceAgentId: string,
      agentVersionId: string,
      expectedLiveVersionSeq: number,
      acknowledgeBaseChange: boolean,
    ): Promise<number>;
  }
}

/**
 * Where a draft came from, as its `agent_version.draft_created` entry recorded it (plan-015 DRF). `pass_targets`
 * ADDED 2026-10-19 (plan-021): a draft saved from the Channels tab's pass targets — warned about like an edit's when
 * its base is no longer live, and never offered "Recreate on the current version". `questionnaire` ADDED 2026-10-28
 * (plan-030, design §41.3.4): a draft saved from a survey's Questions tab, read so the lineage never refuses it.
 */
export type DraftSourceView = "answers" | "optimisation_review" | "edit" | "pass_targets" | "questionnaire";

/** One version's lineage (plan-015 DRF, `GET /v1/agents/{voice_agent_id}/versions`). */
export interface AgentVersionLineageView {
  readonly agentVersionId: string;
  /** The version that was live when this one was made; `null` for a first build or an older version. */
  readonly baseAgentVersionId: string | null;
  readonly baseLabel: string | null;
  /** The base is the live version now. `false` without a base. */
  readonly baseIsLive: boolean;
  readonly draftSource: DraftSourceView | null;
  readonly draftOptimisationReviewId: string | null;
}

/**
 * A `409 draft_base_changed`, as the console can read it. The generated client keeps the envelope's code and message
 * (not its `detail`), and the message is the API's own sentence: "Built on v4; v6 is live now. Publishing replaces
 * v6's changes."
 */
export interface DraftBaseChangedView {
  readonly message: string;
}

/** `409 draft_base_changed`'s sentence, or `null` for any other failure. */
export function draftBaseChangedOf(raised: unknown): DraftBaseChangedView | null {
  return raised instanceof ApiError && raised.code === "draft_base_changed" ? { message: raised.message } : null;
}

const DRAFT_SOURCE: readonly DraftSourceView[] = ["answers", "optimisation_review", "edit", "pass_targets", "questionnaire"];

/** One row of the version list, read for its lineage only. */
export function agentVersionLineageOf(wire: unknown, path: string): AgentVersionLineageView {
  const one = readRecord(wire, path);
  const source = one["draft_source"];
  return {
    agentVersionId: readText(one["agent_version_id"], `${path}.agent_version_id`),
    baseAgentVersionId: readNullableText(one["base_agent_version_id"], `${path}.base_agent_version_id`),
    baseLabel: one["base_label"] === undefined ? null : readNullableText(one["base_label"], `${path}.base_label`),
    baseIsLive: readBoolean(one["base_is_live"], `${path}.base_is_live`),
    draftSource:
      source === null || source === undefined ? null : readOneOf(source, DRAFT_SOURCE, `${path}.draft_source`, portalText("shell", "a_draft_source_40420b6c")),
    draftOptimisationReviewId:
      one["draft_optimisation_review_id"] === undefined
        ? null
        : readNullableText(one["draft_optimisation_review_id"], `${path}.draft_optimisation_review_id`),
  };
}

/**
 * What the sandbox gateway spreads to serve the two methods: it records no base, so every version reads as having none
 * and a draft publishes as `publishAgentVersion` does.
 */
export function draftLineageSandbox(
  agentVersionSource: Pick<ConsoleGateway, "listAgentVersions" | "publishAgentVersion">,
): Required<Pick<ConsoleGateway, "listAgentVersionLineage" | "publishDraftAgentVersion">> {
  return {
    async listAgentVersionLineage(voiceAgentId: string): Promise<readonly AgentVersionLineageView[]> {
      return (await agentVersionSource.listAgentVersions(voiceAgentId)).map((one) => ({
        agentVersionId: one.agentVersionId,
        baseAgentVersionId: null,
        baseLabel: null,
        baseIsLive: false,
        draftSource: null,
        draftOptimisationReviewId: null,
      }));
    },
    publishDraftAgentVersion: (voiceAgentId, agentVersionId, expectedLiveVersionSeq) =>
      agentVersionSource.publishAgentVersion(voiceAgentId, agentVersionId, expectedLiveVersionSeq),
  };
}

export const optimisationReviewGap: readonly GatewayGap[] = [];

const COUNT_FORMAT = () => (new Intl.NumberFormat((portalLocale() === "el" ? "el-GR" : "en-GB")));

/** Every finding card carries the same glyph: the API does not classify findings, so neither does the console. */
const FINDING_ICON = () => ("ph-duotone ph-lightbulb");

const DEPTH: readonly OptimisationReviewDepthView[] = ["standard", "ultra"];
const SCOPE: readonly OptimisationReviewScopeView[] = ["last_24h", "last_7d", "since_last_version"];
const STATE: readonly OptimisationReviewStateView[] = ["idle", "running", "done", "failed"];
const DECISION: readonly OptimisationFindingDecisionView[] = ["pending", "accepted", "rejected"];
const UNAVAILABLE: readonly OptimisationReviewUnavailableReasonView[] = [
  "not_available_yet",
  "not_offered",
  "no_conversations_in_scope",
  "recordings_expired",
  "recording_consent_refused",
  "insufficient_credit",
];
const FAILURE: readonly OptimisationReviewFailureReasonView[] = [
  "insufficient_credit",
  "no_readable_conversations",
  "model_refused",
  "analysis_invalid",
  "took_too_long",
  "not_offered",
  "cancelled_by_operator",
];
const FIX_KIND: readonly OptimisationFixKindView[] = ["answer", "config_patch", "advice"];
const ADVICE_TARGET: readonly OptimisationAdviceTargetView[] = [
  "menu",
  "questionnaire",
  "documents",
  "website",
  "voice",
  "transfer_number",
  "other",
];
const EVIDENCE_LINE_STATE = ["shown", "withheld", "gone"] as const;
const SPEAKER = ["caller", "assistant"] as const;

function readOneOf<T extends string>(value: unknown, allowed: readonly T[], path: string, wanted: string): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, wanted, raw);
  return found;
}

function readNullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

function readMoney(value: unknown, path: string): ReturnType<typeof money> {
  const one = readRecord(value, path);
  return money(
    readWholeNumber(one["amount_minor"], `${path}.amount_minor`),
    readText(one["currency"], `${path}.currency`) as CurrencyCode,
  );
}

/** The `{ data: … }` envelope's payload. */
function dataOf(body: unknown, operationId: string): Readonly<Record<string, unknown>> {
  return readRecord(readRecord(body, operationId)["data"], `${operationId}.data`);
}

function affectedTextOf(count: number): string {
  return portalText("shell", "reviews.calls", { count, figure: COUNT_FORMAT().format(count) });
}

function billingOf(wire: unknown, path: string): OptimisationReviewBillingView {
  const one = readRecord(wire, path);
  const payer = readOneOf(one["payer"], ["customer", "wetalk"] as const, `${path}.payer`, portalText("shell", "a_payer_a887261f"));
  const amount = readMoney(one["amount"], `${path}.amount`);
  if (payer === "customer") return { payer, amount };
  // The domain cannot hold an absorbed review with no reason, so the wire cannot either.
  return { payer, amount, absorbedReason: readText(one["absorbed_reason"], `${path}.absorbed_reason`) };
}

/** plan-032 (design §43.9): the lines a finding rests on, as the api read them for this reader. */
function evidenceLineOf(wire: unknown, path: string, context: SliceContext, now: Date): OptimisationFindingEvidenceLineView {
  const one = readRecord(wire, path);
  const state = readOneOf(one["state"], EVIDENCE_LINE_STATE, `${path}.state`, portalText("shell", "reviews.wanted.evidence_line_state"));
  if (state !== "shown") return { state };
  return {
    state,
    line: readArray(one["line"], `${path}.line`).map((each, index) => {
      const site = `${path}.line[${String(index)}]`;
      const line = readRecord(each, site);
      const startedAt = readText(line["started_at"], `${site}.started_at`);
      return validatedDisplayView({
        conversationId: readText(line["conversation_id"], `${site}.conversation_id`),
        turnIndex: readWholeNumber(line["turn_index"], `${site}.turn_index`),
        speaker: readOneOf(line["speaker"], SPEAKER, `${site}.speaker`, portalText("shell", "reviews.wanted.speaker")),
        text: readText(line["text"], `${site}.text`),
        get startedText() { return context.formatWhen(startedAt, now); },
      });
    }),
  };
}

function nullableOneOf<T extends string>(value: unknown, allowed: readonly T[], path: string, wanted: () => string): T | null {
  return value === null ? null : readOneOf(value, allowed, path, wanted());
}

function findingOf(
  wire: unknown,
  path: string,
  conversationReadCount: number | null,
  context: SliceContext,
  now: Date,
): OptimisationFindingView {
  const one = readRecord(wire, path);
  const affectedConversationCount = readWholeNumber(
    one["affected_conversation_count"],
    `${path}.affected_conversation_count`,
  );
  const evidence = readNullableText(one["evidence"], `${path}.evidence`);
  const proposedFixText = readNullableText(one["proposed_fix_text"], `${path}.proposed_fix_text`);
  return validatedDisplayView({
    optimisationFindingId: readText(one["optimisation_finding_id"], `${path}.optimisation_finding_id`),
    issue: readText(one["issue"], `${path}.issue`),
    get evidence() { return evidence ?? portalText("shell", "reviews.noEvidence"); },
    affectedConversationCount,
    get affectedText() {
      return conversationReadCount === null
        ? affectedTextOf(affectedConversationCount)
        : affectedOfReadText(affectedConversationCount, conversationReadCount);
    },
    get proposedFixText() { return proposedFixText ?? portalText("shell", "reviews.noFix"); },
    decision: readOneOf(one["decision"], DECISION, `${path}.decision`, portalText("shell", "a_finding_decision_9ee9de6f")),
    iconName: FINDING_ICON(),
    category: readNullableText(one["category"], `${path}.category`),
    fixKind: nullableOneOf(one["fix_kind"], FIX_KIND, `${path}.fix_kind`, () => portalText("shell", "reviews.wanted.fix_kind")),
    position: one["position"] === null ? null : readWholeNumber(one["position"], `${path}.position`),
    adviceTarget: nullableOneOf(one["advice_target"], ADVICE_TARGET, `${path}.advice_target`, () =>
      portalText("shell", "reviews.wanted.advice_target"),
    ),
    evidenceLine: evidenceLineOf(one["evidence_line"], `${path}.evidence_line`, context, now),
  });
}

/** One `OptimisationReview` as the panel renders it. */
export function optimisationReviewOf(
  wire: unknown,
  path: string,
  context: SliceContext,
): OptimisationReviewView {
  const one = readRecord(wire, path);
  const now = context.now();
  const startedAt = readNullableText(one["started_at"], `${path}.started_at`);
  const finishedAt = readNullableText(one["finished_at"], `${path}.finished_at`);
  const conversationReadCount =
    one["conversation_read_count"] === null
      ? null
      : readWholeNumber(one["conversation_read_count"], `${path}.conversation_read_count`);
  return validatedDisplayView({
    optimisationReviewId: readText(one["optimisation_review_id"], `${path}.optimisation_review_id`),
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    depth: readOneOf(one["depth"], DEPTH, `${path}.depth`, portalText("shell", "a_review_depth_bbb0036b")),
    scope: readOneOf(one["scope"], SCOPE, `${path}.scope`, portalText("shell", "a_review_scope_42ed3a4b")),
    state: readOneOf(one["state"], STATE, `${path}.state`, portalText("shell", "a_review_state_4b0f4f53")),
    conversationCount: readWholeNumber(one["conversation_count"], `${path}.conversation_count`),
    recordingAvailableCount: readWholeNumber(
      one["recording_available_count"],
      `${path}.recording_available_count`,
    ),
    billing: billingOf(one["billing"], `${path}.billing`),
    finding: readArray(one["finding"], `${path}.finding`).map((each, index) =>
      findingOf(each, `${path}.finding[${index}]`, conversationReadCount, context, now),
    ),
    acceptedCount: readWholeNumber(one["accepted_count"], `${path}.accepted_count`),
    nextAgentVersionLabel: readText(one["next_agent_version_label"], `${path}.next_agent_version_label`),
    get startedText() { return startedAt === null ? null : context.formatWhen(startedAt, now); },
    get finishedText() { return finishedAt === null ? null : context.formatWhen(finishedAt, now); },
    publishedAgentVersionId: readNullableText(
      one["published_agent_version_id"],
      `${path}.published_agent_version_id`,
    ),
    failureReason: nullableOneOf(one["failure_reason"], FAILURE, `${path}.failure_reason`, () =>
      portalText("shell", "reviews.wanted.failure_reason"),
    ),
    conversationReadCount,
  });
}

/** plan-032 (design §43.9): one row of `listOptimisationReviews`. */
export function optimisationReviewSummaryOf(wire: unknown, path: string, context: SliceContext): OptimisationReviewSummaryView {
  const one = readRecord(wire, path);
  const now = context.now();
  const requestedAt = readText(one["requested_at"], `${path}.requested_at`);
  const finishedAt = readNullableText(one["finished_at"], `${path}.finished_at`);
  return validatedDisplayView({
    optimisationReviewId: readText(one["optimisation_review_id"], `${path}.optimisation_review_id`),
    depth: readOneOf(one["depth"], DEPTH, `${path}.depth`, portalText("shell", "a_review_depth_bbb0036b")),
    scope: readOneOf(one["scope"], SCOPE, `${path}.scope`, portalText("shell", "a_review_scope_42ed3a4b")),
    state: readOneOf(one["state"], STATE, `${path}.state`, portalText("shell", "a_review_state_4b0f4f53")),
    failureReason: nullableOneOf(one["failure_reason"], FAILURE, `${path}.failure_reason`, () =>
      portalText("shell", "reviews.wanted.failure_reason"),
    ),
    findingCount: readWholeNumber(one["finding_count"], `${path}.finding_count`),
    get requestedText() { return context.formatWhen(requestedAt, now); },
    get finishedText() { return finishedAt === null ? null : context.formatWhen(finishedAt, now); },
    publishedAgentVersionId: readNullableText(one["published_agent_version_id"], `${path}.published_agent_version_id`),
  });
}

/** The quote, with each depth card's copy from `DEPTH_COPY` and its price from the API. */
export function optimisationReviewQuoteOf(wire: unknown, path: string): OptimisationReviewQuoteView {
  const one = readRecord(wire, path);
  const priced = new Map<string, Readonly<Record<string, unknown>>>();
  readArray(one["option"], `${path}.option`).forEach((each, index) => {
    const option = readRecord(each, `${path}.option[${index}]`);
    priced.set(readText(option["depth"], `${path}.option[${index}].depth`), option);
  });
  const option = DEPTH_COPY.map((copy): OptimisationReviewDepthOptionView => {
    const found = priced.get(copy.depth);
    const site = `${path}.option[${copy.depth}]`;
    if (found === undefined) throw new MalformedResponseError(site, "a priced depth", undefined);
    const reason = found["unavailable_reason"];
    return {
      depth: copy.depth,
      name: copy.name,
      dek: copy.dek,
      point: copy.point,
      price: readMoney(found["price"], `${site}.price`),
      unavailableReason:
        reason === null
          ? null
          : readOneOf(reason, UNAVAILABLE, `${site}.unavailable_reason`, portalText("shell", "an_unavailable_reason_1b5873b5")),
    };
  });
  return {
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    scope: readOneOf(one["scope"], SCOPE, `${path}.scope`, portalText("shell", "a_review_scope_42ed3a4b")),
    conversationCount: readWholeNumber(one["conversation_count"], `${path}.conversation_count`),
    recordingAvailableCount: readWholeNumber(
      one["recording_available_count"],
      `${path}.recording_available_count`,
    ),
    option,
  };
}

/** A fresh key per start; see "THE Idempotency-Key ON START" above. */
function startIdempotencyKey(): string {
  return `optimisation-review-${globalThis.crypto.randomUUID()}`;
}

export function optimisationReviewApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<
  ConsoleGateway,
  | "getOptimisationReviewQuote"
  | "startOptimisationReview"
  | "getOptimisationReview"
  | "listOptimisationReviews"
  | "decideOptimisationFinding"
  | "publishOptimisationReview"
  | "listAgentVersionLineage"
  | "publishDraftAgentVersion"
> {
  return {
    async getOptimisationReviewQuote(
      voiceAgentId: string,
      scope: OptimisationReviewScopeView,
    ): Promise<OptimisationReviewQuoteView> {
      const body = assertOk(
        await client.getOptimisationReviewQuote({
          path: { voice_agent_id: voiceAgentId },
          query: { scope },
        }),
      );
      return optimisationReviewQuoteOf(
        readRecord(body, "getOptimisationReviewQuote")["data"],
        "getOptimisationReviewQuote.data",
      );
    },

    async startOptimisationReview(
      request: StartOptimisationReviewRequestView,
    ): Promise<OptimisationReviewView> {
      const body = assertOk(
        await client.createOptimisationReview({
          path: { voice_agent_id: request.voiceAgentId },
          body: {
            depth: request.depth,
            scope: request.scope,
            payer: request.payer,
            absorbed_reason: request.payer === "wetalk" ? request.absorbedReason : null,
          },
          header: { "Idempotency-Key": startIdempotencyKey() },
        }),
      );
      return optimisationReviewOf(dataOf(body, "createOptimisationReview"), "createOptimisationReview.data", context);
    },

    async getOptimisationReview(optimisationReviewId: string): Promise<OptimisationReviewView> {
      const body = assertOk(
        await client.getOptimisationReview({ path: { optimisation_review_id: optimisationReviewId } }),
      );
      return optimisationReviewOf(dataOf(body, "getOptimisationReview"), "getOptimisationReview.data", context);
    },

    async listOptimisationReviews(voiceAgentId: string, cursor: string | null): Promise<OptimisationReviewPageView> {
      const body = readRecord(
        assertOk(
          await client.listOptimisationReviews({
            path: { voice_agent_id: voiceAgentId },
            query: cursor === null ? {} : { cursor },
          }),
        ),
        "listOptimisationReviews",
      );
      return {
        row: readArray(body["data"], "listOptimisationReviews.data").map((row, index) =>
          optimisationReviewSummaryOf(row, `listOptimisationReviews.data[${String(index)}]`, context),
        ),
        nextCursor: readNullableText(body["next_cursor"], "listOptimisationReviews.next_cursor"),
      };
    },

    async decideOptimisationFinding(
      optimisationReviewId: string,
      optimisationFindingId: string,
      decision: OptimisationFindingDecisionView,
    ): Promise<OptimisationReviewView> {
      const body = assertOk(
        await client.decideOptimisationFinding({
          path: {
            optimisation_review_id: optimisationReviewId,
            optimisation_finding_id: optimisationFindingId,
          },
          body: { decision },
        }),
      );
      return optimisationReviewOf(
        dataOf(body, "decideOptimisationFinding"),
        "decideOptimisationFinding.data",
        context,
      );
    },

    async publishOptimisationReview(
      optimisationReviewId: string,
      expectedLiveVersionSeq: number,
    ): Promise<PublishedOptimisationReviewView> {
      const data = dataOf(
        assertOk(
          await client.publishOptimisationReview({
            path: { optimisation_review_id: optimisationReviewId },
            body: { expected_live_version_seq: expectedLiveVersionSeq },
            header: { "Idempotency-Key": `optimisation-review-publish-${optimisationReviewId}-${String(expectedLiveVersionSeq)}` },
          }),
        ),
        "publishOptimisationReview",
      );
      return {
        optimisationReviewId: readText(data["optimisation_review_id"], "publishOptimisationReview.optimisation_review_id"),
        agentVersionId: readText(data["agent_version_id"], "publishOptimisationReview.agent_version_id"),
        liveVersionSeq: readWholeNumber(data["live_version_seq"], "publishOptimisationReview.live_version_seq"),
        appliedFindingCount: readWholeNumber(
          data["applied_finding_count"],
          "publishOptimisationReview.applied_finding_count",
        ),
      };
    },

    async listAgentVersionLineage(voiceAgentId: string): Promise<readonly AgentVersionLineageView[]> {
      const body = readRecord(
        assertOk(await client.listAgentVersions({ path: { voice_agent_id: voiceAgentId } })),
        "listAgentVersions",
      );
      return readArray(body["data"], "listAgentVersions.data").map((row, index) =>
        agentVersionLineageOf(row, `listAgentVersions.data[${String(index)}]`),
      );
    },

    async publishDraftAgentVersion(
      voiceAgentId: string,
      agentVersionId: string,
      expectedLiveVersionSeq: number,
      acknowledgeBaseChange: boolean,
    ): Promise<number> {
      const data = dataOf(
        assertOk(
          await client.publishAgentVersion({
            path: { voice_agent_id: voiceAgentId, agent_version_id: agentVersionId },
            body: { expected_live_version_seq: expectedLiveVersionSeq, acknowledge_base_change: acknowledgeBaseChange },
          }),
        ),
        "publishAgentVersion",
      );
      return readWholeNumber(data["live_version_seq"], "publishAgentVersion.data.live_version_seq");
    },
  };
}
